import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Spacer,
  Text,
  useDisclosure
} from "@chakra-ui/react";
import { Formik } from "formik";
import { MdFilterList } from "react-icons/md";
import { Icon } from "@chakra-ui/icons";
import { trimOuterPlusSigns } from "../../util/trimOuterPlusSigns.js";
export const validDateValues = ["equals", "before", "after", "between"];
export const statuses = [
  "completed",
  "canceled",
  "failed",
  "running",
  "submitted",
  "ready"
];
export const FilterButton = ({ filters, setFilters, filterOptions = [] }) => {
  const { onOpen, onClose, isOpen } = useDisclosure();
  const isFiltering = Object.keys(filters).map((x) => filters[x]).filter(Boolean).length;
  const params = new URLSearchParams(window.location.search);
  const urlFilterDate = params.get("date")?.split("=")[1]?.split("|")?.[0] ?? null;
  const urlFilterEndDate = params.get("date")?.split("=")[1]?.split("|")?.[1] ?? null;
  const urlDate = params.get("date")?.split("=")?.[0];
  const hasUrlDate = urlDate && urlFilterDate && validDateValues.includes(urlDate);
  const urlName = trimOuterPlusSigns(params.get("name"));
  const urlProjectId = params.get("projectId");
  const urlStatus = params.get("status");
  const hasUrlStatus = statuses.includes(urlStatus);
  const urlId = params.get("id");
  const urlMetadataKey = params.get("metadataKey");
  const urlMetadataValue = params.get("metadataValue");
  const defaultValues = {
    status: urlStatus || "",
    name: urlName || "",
    id: urlId || "",
    dateCheckbox: !!urlDate,
    statusCheckbox: !!urlStatus,
    nameCheckbox: !!urlName,
    metadataCheckbox: !!urlMetadataKey,
    idCheckbox: !!urlId,
    filterDate: urlFilterDate || "",
    filterEndDate: urlFilterEndDate || "",
    metadataKey: urlMetadataKey || "",
    metadataValue: urlMetadataValue || "",
    projectId: urlProjectId || "",
    projectIdCheckbox: !!urlProjectId
  };
  return /* @__PURE__ */ React.createElement(
    Popover,
    {
      placement: "bottom-start",
      isOpen,
      onOpen,
      onClose
    },
    /* @__PURE__ */ React.createElement(PopoverTrigger, null, /* @__PURE__ */ React.createElement(
      Button,
      {
        "data-testid": "FilterButton",
        size: "md",
        color: isFiltering ? "uiBlue" : "unset",
        variant: "grey",
        leftIcon: /* @__PURE__ */ React.createElement(
          Icon,
          {
            as: MdFilterList,
            width: "16px",
            height: "16px",
            color: isFiltering ? "uiBlue" : "gray.600"
          }
        )
      },
      isFiltering ? "Filtering" : "Filter"
    )),
    /* @__PURE__ */ React.createElement(Portal, null, /* @__PURE__ */ React.createElement(
      PopoverContent,
      {
        maxWidth: "300px",
        "data-testid": "FilterButtonPopover"
      },
      /* @__PURE__ */ React.createElement(PopoverBody, { m: 0, p: 0 }, /* @__PURE__ */ React.createElement(
        Formik,
        {
          initialValues: defaultValues,
          onSubmit: (values) => {
            const trimmedValues = {
              ...values,
              name: trimOuterPlusSigns(values.name)
            };
            setFilters(trimmedValues);
          }
        },
        ({
          dirty,
          handleChange,
          handleBlur,
          values,
          setValues
        }) => {
          return /* @__PURE__ */ React.createElement(
            Box,
            {
              borderRadius: "5px",
              bg: "white",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
            },
            /* @__PURE__ */ React.createElement(
              Flex,
              {
                borderRadius: "5px",
                borderBottomEndRadius: 0,
                borderBottomStartRadius: 0,
                padding: "8px 16px 0",
                border: "1px solid #e8e8e8",
                bg: "#f3f3f3",
                maxHeight: "46px"
              },
              /* @__PURE__ */ React.createElement(
                Button,
                {
                  "data-testid": "FilterClearButton",
                  size: "sm",
                  variant: "grey",
                  onClick: () => {
                    const urlParams = new URLSearchParams(
                      window.location.search
                    );
                    [
                      "id",
                      "name",
                      "date",
                      "filterEndDate",
                      "filterDate",
                      "status",
                      "projectId",
                      "metadataKey",
                      "metadataValue"
                    ].map((param) => {
                      urlParams.delete(param);
                    });
                    window.location.href = `${window.location.href.split("?")[0]}?${urlParams.toString()}`;
                  }
                },
                "Clear"
              ),
              /* @__PURE__ */ React.createElement(Spacer, null),
              /* @__PURE__ */ React.createElement(
                Button,
                {
                  "data-testid": "FilterApplyButton",
                  size: "sm",
                  variant: "blue",
                  mb: 3,
                  type: "submit",
                  onClick: () => {
                    const urlParams = new URLSearchParams(
                      window.location.search
                    );
                    const lookup = (param) => values?.[param] || filters?.[param];
                    const params2 = new URLSearchParams({});
                    const url = `${window.location.href.split("?")[0]}?`;
                    if (values?.date && values?.filterDate) {
                      params2.set(
                        "date",
                        `${lookup("date")}=${lookup("filterDate")}${lookup(
                          "filterEndDate"
                        ) && lookup(
                          "date"
                        ) === "between" ? `|${lookup(
                          "filterEndDate"
                        )}` : ""}`
                      );
                    }
                    if (values.id) {
                      params2.set(
                        "id",
                        lookup("id")
                      );
                    }
                    if (values.name) {
                      const trimmedName = trimOuterPlusSigns(
                        lookup("name")
                      );
                      params2.set(
                        "name",
                        trimmedName
                      );
                    }
                    if (values.status) {
                      params2.set(
                        "status",
                        lookup("status")
                      );
                    }
                    if (values.projectId) {
                      params2.set(
                        "projectId",
                        lookup("projectId")
                      );
                    }
                    if (values.metadataKey) {
                      params2.set(
                        "metadataKey",
                        lookup(
                          "metadataKey"
                        )
                      );
                      if (values.metadataValue) {
                        params2.set(
                          "metadataValue",
                          lookup(
                            "metadataValue"
                          )
                        );
                      }
                    }
                    if (params2.toString() !== urlParams.toString()) {
                      window.location.href = `${url}${decodeURIComponent(params2.toString())}`;
                    }
                  }
                },
                "Apply"
              )
            ),
            filterOptions.map(
              ({
                name,
                options,
                property,
                isDate,
                isMetadata,
                label
              }) => /* @__PURE__ */ React.createElement(
                CheckboxSelectInputFormFilter,
                {
                  dirty,
                  key: name,
                  isMetadata,
                  isDate,
                  handleBlur,
                  handleChange,
                  options,
                  name,
                  property,
                  values: {
                    ...values,
                    id: urlId ? urlId : values.id,
                    idCheckbox: urlId ? true : values.idCheckbox,
                    date: urlDate ? urlDate : values.date,
                    dateCheckbox: hasUrlDate ? true : values.dateCheckbox,
                    filterDate: hasUrlDate ? urlFilterDate : values.filterDate,
                    filterEndDate: hasUrlDate && urlFilterEndDate ? urlFilterEndDate : values.filterEndDate,
                    metadataKey: urlMetadataKey ? urlMetadataKey : values.metadataKey,
                    metadataValue: urlMetadataValue ? urlMetadataValue : values.metadataValue,
                    name: urlName ? urlName : values.name,
                    nameCheckbox: urlName ? true : values.nameCheckbox,
                    projectId: urlProjectId ? urlProjectId : values.projectId,
                    projectIdCheckbox: urlProjectId ? true : values.projectIdCheckbox,
                    status: hasUrlStatus ? urlStatus : values.status,
                    statusCheckbox: hasUrlStatus ? true : values.statusCheckbox
                  },
                  setValues,
                  label
                }
              )
            )
          );
        }
      ))
    ))
  );
};
const CheckboxSelectInputFormFilter = ({
  handleBlur,
  handleChange,
  isDate = false,
  isMetadata = false,
  name = "",
  options,
  property,
  values,
  dirty,
  setValues,
  label = ""
}) => {
  const [useLocalValues, setUseLocalValues] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(null);
  const [localFilterDate, setLocalFilterDate] = React.useState(null);
  const [localFilterEndDate, setLocalFilterEndDate] = React.useState(null);
  const checkboxName = `${property}Checkbox`;
  const isSelect = Boolean(options?.length);
  const [useLocalCheckbox, setUseLocalCheckbox] = React.useState(false);
  const [localCheckboxValue, setLocalCheckboxValue] = React.useState(false);
  const [localMetadataValue, setLocalMetadataValue] = React.useState(null);
  return /* @__PURE__ */ React.createElement(
    Box,
    {
      borderRadius: "0",
      padding: "8px 16px",
      border: "1px solid #e8e8e8",
      borderTop: "0px"
    },
    /* @__PURE__ */ React.createElement(
      Checkbox,
      {
        "data-testid": `Checkbox-${name}`,
        name: checkboxName,
        colorScheme: "blue",
        size: "sm",
        fontWeight: "500",
        color: "gray.500",
        onChange: (e) => {
          setUseLocalCheckbox(true);
          setLocalCheckboxValue(e.target.checked);
          setValues({ ...values, [property]: "" });
          handleChange(e);
        },
        onBlur: handleBlur,
        isChecked: useLocalCheckbox ? localCheckboxValue : values[checkboxName]
      },
      name
    ),
    (useLocalCheckbox ? localCheckboxValue : values?.[checkboxName]) ? isDate ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      Select,
      {
        size: "xs",
        name: property,
        onChange: (e) => {
          setUseLocalValues(true);
          setLocalValue(e.target.value);
          handleChange(e);
        },
        onBlur: handleBlur,
        value: useLocalValues ? localValue : values[property]
      },
      ["", ...validDateValues].map((item) => {
        return /* @__PURE__ */ React.createElement(
          "option",
          {
            key: item.toLowerCase(),
            value: item.toLowerCase()
          },
          item ? item : `Choose setting`
        );
      })
    ), /* @__PURE__ */ React.createElement(Flex, { mt: "8px" }, /* @__PURE__ */ React.createElement(
      Input,
      {
        "data-testid": `Input-${name}`,
        size: "xs",
        display: "inline-block",
        name: "filterDate",
        value: localFilterDate ? localFilterDate : values["filterDate"],
        onChange: (e) => {
          setLocalFilterDate(e.target.value);
          handleChange(e);
        },
        onBlur: handleBlur,
        isInvalid: localFilterDate && (localFilterDate.length !== 10 || !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(
          localFilterDate
        )) || dirty.filterDate && (values.filterDate?.length !== 10 || !values.filterDate.test(
          /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/
        )),
        placeholder: "YYYY-MM-DD"
      }
    ), (useLocalValues ? localValue : values[property]) === "between" && /* @__PURE__ */ React.createElement(
      Input,
      {
        size: "xs",
        display: "inline-block",
        ml: "8px",
        value: localFilterEndDate ? localFilterEndDate : values["filterEndDate"],
        name: "filterEndDate",
        isInvalid: localFilterEndDate && (localFilterEndDate.length !== 10 || !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(
          localFilterEndDate
        )) || dirty.filterEndDate && (values.filterEndDate?.length !== 10 || !values.filterEndDate.test(
          /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/
        )),
        onChange: (e) => {
          setLocalFilterEndDate(e.target.value);
          handleChange(e);
        },
        onBlur: handleBlur,
        placeholder: "YYYY-MM-DD"
      }
    ))) : isSelect ? /* @__PURE__ */ React.createElement(
      Select,
      {
        size: "xs",
        name: property,
        onChange: (e) => {
          setUseLocalValues(true);
          setLocalValue(e.target.value);
          handleChange(e);
        },
        onBlur: handleBlur,
        value: useLocalValues ? localValue : values[property]
      },
      [""].concat(options).map((item) => {
        if (typeof item === "string") {
          return /* @__PURE__ */ React.createElement(
            "option",
            {
              key: item.toLowerCase(),
              value: item.toLowerCase()
            },
            item ? item : `Choose ${name}`
          );
        }
        return /* @__PURE__ */ React.createElement(
          "option",
          {
            key: item.value.toLowerCase(),
            value: item.value.toLowerCase()
          },
          item.label ? item.label : `Choose ${name}`
        );
      })
    ) : isMetadata ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Text, { textStyle: "body-9", mb: "4px" }, "key"), /* @__PURE__ */ React.createElement(
      Input,
      {
        size: "sm",
        name: "metadataKey",
        onChange: (e) => {
          setUseLocalValues(true);
          setLocalValue(e.target.value);
          handleChange(e);
        },
        onBlur: handleBlur,
        value: localValue ? localValue : values["metadataKey"]
      }
    ), values.metadataKey && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Text, { textStyle: "body-9", mt: "8px", mb: "4px" }, "value (optional)"), /* @__PURE__ */ React.createElement(
      Input,
      {
        size: "sm",
        name: "metadataValue",
        onChange: (e) => {
          setLocalMetadataValue(e.target.value);
          handleChange(e);
        },
        onBlur: handleBlur,
        value: localMetadataValue ? localMetadataValue : values["metadataValue"]
      }
    ))) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Text, { textStyle: "body-9", mb: "4px" }, label), /* @__PURE__ */ React.createElement(
      Input,
      {
        "data-testid": `Input-${name}`,
        size: "sm",
        name: property,
        onChange: (e) => {
          setUseLocalValues(true);
          setLocalValue(e.target.value);
          handleChange(e);
        },
        onBlur: handleBlur,
        value: useLocalValues ? localValue : values[property]
      }
    )) : null
  );
};
